import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import {
  AgreementForTicket,
  AgreementItem,
  AgreementItemMinimal,
  RegistryItem,
  RegistryItemEssential,
  ScheduleCreateDto,
  ScheduleItem,
  ScheduleStatusItem,
  ScheduleUpdateDto,
  TicketItem,
  UserItem,
  UserItemMinimal,
  WorkflowTransitionEmailConfig,
} from 'src/app/api/models';
import { CommonService, SchedulazioniService, TicketService } from 'src/app/api/services';
import { manipulateToUTCDate, manipulateToUTCISOString } from 'src/main';
import { ModalRegPickerComponent } from '../modal-reg-picker/modal-reg-picker.component';
import { RegistryTypeEnum } from '../enums/registry_type_enum.enum';
import { ModalUserPickerComponent } from '../modal-user-picker/modal-user-picker.component';
import { environment } from 'src/environments/environment';
import { BaseUploadComponent } from 'src/app/utils/base-upload.component';
import { AttachmentType } from '../modal-upload/modal-upload.component';
import { formatDate } from '@angular/common';
import { SendEmailGenericComponent } from 'src/app/auth/send-email-generic/send-email-generic.component';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { checkGrants } from '../directives/grant.directive';
import { GrantsService } from 'src/app/services/grants.service';

export const durationValidator: ValidatorFn = c => {
  const durationControl = c as FormControl;
  const scheduleStatusControl = durationControl.parent?.get(
    'schedule_status'
  ) as FormControl | null;

  if (scheduleStatusControl?.value?.id === 4 && (durationControl.value ?? 0) < 1) {
    return { durationInvalid: 'Per chiudere una schedulazione, inserire una durata valida..' };
  } else {
    return null;
  }
};

@Component({
  selector: 'app-modal-manage-schedules',
  templateUrl: './modal-manage-schedules.component.html',
  styleUrls: ['./modal-manage-schedules.component.scss'],
})
export class ModalManageSchedulesComponent extends BaseUploadComponent {
  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  environment = environment;
  override type: AttachmentType = 'schedule';
  tbl_actions: MenuItem[] | undefined;
  isEditMode = false;
  isLoading = false;
  selectedSchedule?: ScheduleItem;
  ticketID?: number;
  schedules: ScheduleItem[] = [];
  scheduleStatuses: ScheduleStatusItem[] = [];
  totalRecords = 0;
  loading = true;
  registriesPickerRef: DynamicDialogRef | undefined;
  contactPickerRef: DynamicDialogRef | undefined;
  selectedAssignedUser?: UserItemMinimal;
  minSelectionDate: Date = new Date();
  selectedRegistries: RegistryItem[] = [];
  targetTicket?: TicketItem;
  targetSchedule?: ScheduleItem;
  targetMaintenanceRegistry: RegistryItem[] = [];
  formTitle = 'Associa schedulazione';
  @ViewChild('scheduleTable') table?: Table;

  menuItems: MenuItem[] | undefined;

  home: MenuItem | undefined;
  scheduleConfiguration?: WorkflowTransitionEmailConfig;

  constructor(
    private title: Title,
    private activatedRoute: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private ticketService: TicketService,
    private commonService: CommonService,
    private scheduleService: SchedulazioniService,
    private grantsService: GrantsService,
    private messageService: MessageService
  ) {
    super();
    // if (config.data!.initial_value) {
    //   this.scheduleForm.get('name')!.setValue(config.data!.initial_value.name);
    //   this.scheduleForm.get('maintenance')!.setValue(config.data!.initial_value.maintenance);
    // }
  }

  scheduleForm = new FormGroup({

      remote_support: new FormControl<boolean | undefined>(false, { nonNullable: true }),

    related_registry: new FormControl<RegistryItemEssential | undefined>(undefined, {
      validators: Validators.required,
      nonNullable: true,
    }),
    assigned_user: new FormControl<UserItemMinimal | undefined>(undefined),
    assignment_date: new FormControl<Date | undefined>(undefined, {
      validators: Validators.required,
      nonNullable: true,
    }),
    intervention_request_date: new FormControl<Date | undefined>(undefined, {
      validators: Validators.required,
      nonNullable: true,
    }),
    duration: new FormControl<number | undefined>(0, {
      validators: [Validators.required, durationValidator],
      nonNullable: true,
    }),
    customer_appointment: new FormControl<boolean | undefined>(false, { nonNullable: true }),
    notes: new FormControl<string | undefined>(undefined),
    related_ticket_id: new FormControl<number | undefined>(undefined, {
      validators: Validators.required,
      nonNullable: true,
    }),
    schedule_status: new FormControl<ScheduleStatusItem | undefined>(
      { active: true, id: 1, label: 'Richiesta' },
      {
        validators: Validators.required,
        nonNullable: true,
      }
    ),
    price: new FormControl<number | undefined>(undefined, {
      validators: Validators.required,
      nonNullable: true,
    }),
    hours_minutes: new FormControl<string | undefined>(undefined),
  });

  ngOnInit() {
    this.title.setTitle(`${environment.softwareName} - Schedulazioni`);
    this.home = { icon: 'pi pi-home', routerLink: '/main/home' };
    this.commonService.commonControllerGetScheduleConfiguration().subscribe({
      next: ({ data }) => {
        this.scheduleConfiguration = JSON.parse(data) as WorkflowTransitionEmailConfig;
      },
    });
    this.activatedRoute.params.subscribe(async p => {
      this.ticketID = p['id'];

      if (this.ticketID) {
        await this.fetchOneTicket();
        this.loadAttachments();
        this.scheduleForm.get('related_ticket_id')?.setValue(this.ticketID);
        this.scheduleForm.get('assignment_date')?.setValue(manipulateToUTCDate(new Date()));
        this.fetchSchedules(this.table?.createLazyLoadMetadata());
        this.fetchScheduleStatuses();
        this.menuItems = [
          { label: 'Ticket' },
          { label: 'Elenco ticket', routerLink: '/main/ticket' },
          {
            label: `Dettaglio ticket ${this.targetTicket?.ticket_number} / ${this.targetTicket?.ticket_subnumber}`,
            routerLink: `/main/ticket/dettaglio-ticket/${this.ticketID}`,
          },
          { label: 'Schedulazioni' },
        ];
        console.log(this.schedules);

        this.scheduleForm.get('assignment_date')?.valueChanges.subscribe({
          next: res => {
            this.minSelectionDate = res!;
          },
        });
      }
      this.scheduleForm.get('schedule_status')?.valueChanges.subscribe({
        next: val => {
          this.scheduleForm.get('duration')?.updateValueAndValidity();
        },
      });
      this.scheduleForm.get('duration')?.valueChanges.subscribe({
        next: val => {
          const formattedTime = this.fromMinutesToHoursAndMinutes(val!);
          this.scheduleForm.get('hours_minutes')?.setValue(formattedTime, { emitEvent: false });
        },
      });
    });

    this.scheduleForm.get('remote_support')?.valueChanges.subscribe({
      next: (remote_support) => {
        const priceControl = this.scheduleForm.get('price');
        const statusControl = this.scheduleForm.get('schedule_status');
        const remoteSupportControl = this.scheduleForm.get('remote_support');
        if (remote_support === true) {
          priceControl?.setValue(0, { emitEvent: false });
          priceControl?.disable({ emitEvent: false });
          const remoteStatus = this.scheduleStatuses.find(s => s.id === 4);
          statusControl?.setValue(remoteStatus ?? ({ id: 2 } as ScheduleStatusItem), { emitEvent: false });
          statusControl?.disable({ emitEvent: false });
          if (this.isEditMode) {
            remoteSupportControl?.disable({ emitEvent: false });
          }
        } else {
          priceControl?.enable({ emitEvent: false });
          statusControl?.enable({ emitEvent: false });
          const remoteStatus = this.scheduleStatuses.find(s => s.id === 1);
          statusControl?.setValue(remoteStatus ?? ({ id: 1 } as ScheduleStatusItem), { emitEvent: false });
        }
      },
    });
  }

  fromMinutesToHoursAndMinutes(minutes: number) {
    if (minutes !== null && minutes !== undefined) {
      const ore = Math.floor(minutes / 60);
      const minuti = minutes % 60;

      const formattedTime = `${ore.toString().padStart(2, '0')}:${minuti
        .toString()
        .padStart(2, '0')}`;

      return formattedTime;
    }

    return null;
  }

  confirmationDeleteDialog(schedule_id: number, event: Event) {
    event.stopPropagation();
    this.confirmationService.confirm({
      message: 'Sei sicuro di voler eliminare la schedulazione selezionata?',
      header: 'Attenzione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì',
      accept: () => {
        this.scheduleService.scheduleControllerDeleteSchedule({ id: schedule_id }).subscribe({
          next: res => {
            this.fetchSchedules(this.table?.createLazyLoadMetadata());
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminazione',
              detail: 'La schedulazione è stata eliminata.',
            });
          },
          error: err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Eliminazione schedulazione',
              detail: err.error.message,
            });
          },
        });
      },
    });
  }
  confirmationCustomerAppointmentDialog() {
    return new Promise<void>((resolve, reject) => {
      const ref = this.confirmationService.confirm({
        message:
          "Hai indicato che c'è un appuntamento preso con il cliente, questo interromperà lo SLA sul ticket, confermi?",
        header: 'Conferma',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sì',
        rejectLabel: 'No',
        closeOnEscape: false,
        dismissableMask: false,
        accept: () => {
          resolve();
        },
        reject: () => {
          reject(new Error('Cliccato su No'));
        },
      });
    });
  }

  fetchOneTicket(): Promise<TicketItem> {
    return new Promise<TicketItem>((resolve, reject) =>
      this.ticketService.ticketControllerFindOne({ id: this.ticketID! }).subscribe({
        next: res => {
          this.targetTicket = res.data;
          resolve(this.targetTicket);
        },
        error: error => {
          reject(error);
        },
      })
    );
  }

  fetchScheduleStatuses() {
    this.scheduleService.scheduleControllerFindScheduleStatuses().subscribe({
      next: res => {
        this.scheduleStatuses = res.data.list;
      },
    });
  }

  fetchSchedules(event: TableLazyLoadEvent) {
    this.scheduleService
      .scheduleControllerFindAllSchedules({ related_ticket_id: this.ticketID })
      .subscribe(res => {
        this.schedules = res.data.list.map(s => ({
          ...s,
          duration: this.fromMinutesToHoursAndMinutes(s.duration) as any,
        }));
        console.log('this.schedules', this.schedules);
        this.totalRecords = res.data.count;
        this.loading = false;
      });
  }

  updateSchedule(schedule_id: number) {
    this.isEditMode = true;
    this.scheduleService.scheduleControllerFindOneSchedule({ id: schedule_id }).subscribe({
      next: res => {
        console.log('targetSchedule', this.targetSchedule);
        this.targetSchedule = res.data;
        // PATCH
        this.scheduleForm.patchValue({
          customer_appointment: this.targetSchedule.customer_appointment,
          schedule_status: this.targetSchedule.related_schedule_status,
          duration: this.targetSchedule.duration,
          notes: this.targetSchedule.notes,
          intervention_request_date: manipulateToUTCDate(
            new Date(this.targetSchedule.intervention_request_date!)
          ),
          price: this.targetSchedule.price,
          assigned_user: this.targetSchedule.assigned_user,
          related_registry: this.targetSchedule.related_registry,
          remote_support: (this.targetSchedule as any)['remote_support'] ?? false,
        });
      },
    });
  }

  resetForm() {
    this.isEditMode = false;
    const currentTicketId = this.scheduleForm.get('related_ticket_id')?.value;
    this.scheduleForm.reset();
    this.scheduleForm.patchValue({
      related_ticket_id: currentTicketId,
      assignment_date: manipulateToUTCDate(new Date()),
    });
  }

  openRegistryModal() {
    this.registriesPickerRef = this.dialogService.open(ModalRegPickerComponent, {
      data: {
        question_title: 'Seleziona le società',
        chosenRegistries: this.selectedRegistries,
        selectedRegistry: this.selectedRegistries[0],
        pickerType: 'radio',
        contactTypes: [RegistryTypeEnum.FORNITORE],
        mainteinanceAgreementId: this.targetTicket?.related_maintenance_agreement?.id,
        // service_id: this.targetTicket?.related_maintenance_product_service_and_pricing?.related_service?.id
      },
      header: 'Seleziona azienda addetta alla manutezione',
      width: '90%',
    });

    this.registriesPickerRef.onClose.subscribe((r: { value: RegistryItem }) => {
      if (r && r.value) {
        const selectedRegistry = r.value;

        this.selectedRegistries = [selectedRegistry];

        this.scheduleForm.get('related_registry')!.setValue(selectedRegistry);
      }
    });
  }

  openAssignedUserModal() {
    this.contactPickerRef = this.dialogService.open(ModalUserPickerComponent, {
      data: {
        question_title: 'Seleziona il tecnico assegnatario',
        chosenUser: this.selectedAssignedUser,
        registry_id: this.selectedRegistries[0]?.id,
        isTechnician: true,
      },
      header: 'Assegna tecnico',
      width: '900px',
    });
    this.contactPickerRef.onClose.subscribe((a: { value: UserItem }) => {
      if (a) {
        this.selectedAssignedUser = a.value;
        this.scheduleForm.get('assigned_user')!.setValue(this.selectedAssignedUser);
      }
    });
  }

  async save() {
    //PATCH
    try {
      if (this.isEditMode) {
        const formValue = this.scheduleForm.getRawValue();
        if (formValue.customer_appointment && this.targetTicket?.ticket_status.id !== 58) {
          await this.confirmationCustomerAppointmentDialog();
        }
        this.isLoading = true;
        const body = {
          schedule_status_id: formValue.schedule_status?.id,
          duration: formValue.duration ?? undefined,
          notes: formValue.notes ?? undefined,
          customer_appointment: formValue.customer_appointment || false,
          intervention_request_date:
            manipulateToUTCISOString(formValue.intervention_request_date!, false) ?? undefined,
          price: formValue.price ?? undefined,
          assigned_user_id: formValue.assigned_user?.id ?? undefined,
          remote_support: formValue.remote_support ?? false,
        } as ScheduleUpdateDto;

        this.scheduleService
          .scheduleControllerUpdateSchedule({ id: this.targetSchedule!.id, body })
          .subscribe({
            next: res => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Modifica schedulazione',
                detail: 'Schedulazione modificata correttamente.',
              });

              this.fetchSchedules(this.table?.createLazyLoadMetadata());
            },
            error: err => {
              this.isLoading = false;
              if (err.status === 403) {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Modifica schedulazione',
                  detail: err.error.message,
                });
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Modifica schedulazione',
                  detail: 'Errore durante la modifica della schedulazione.',
                });
              }
            },
          });
      } else {
        const formValue = this.scheduleForm.getRawValue();

        if (
          !this.targetSchedule?.customer_appointment &&
          this.scheduleForm.get('customer_appointment')?.value
        ) {
          await this.confirmationCustomerAppointmentDialog();
        }
        this.isLoading = true;
        //POST
        const body: ScheduleCreateDto = {
          assignment_date: manipulateToUTCISOString(formValue.assignment_date!, false),
          intervention_request_date: manipulateToUTCISOString(
            formValue.intervention_request_date!,
            false
          ),
          customer_appointment: formValue.customer_appointment!,
          schedule_status_id: formValue.schedule_status?.id!,
          duration: formValue.duration!,
          notes: formValue.notes ?? undefined,
          related_ticket_id: Number(formValue.related_ticket_id!),
          assigned_user_id: formValue.assigned_user?.id ?? undefined,
          price: formValue.price!,
          related_registry_id: formValue.related_registry?.id!,
          remote_support:formValue.remote_support!

          // schedule_status_id: 1,
        };
        this.scheduleService.scheduleControllerCreateSchedule({ body }).subscribe({
          next: res => {
            this.isLoading = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Creazione schedulazione',
              detail: 'Schedulazione creata correttamente.',
            });

            this.selectedSchedule = res.data;
            this.sendEmail(res.data);
            this.resetForm();
            this.fetchSchedules(this.table?.createLazyLoadMetadata());
          },
          error: err => {
            this.isLoading = false;
            if (err.status === 403) {
              this.messageService.add({
                severity: 'error',
                summary: 'Creazione schedulazione',
                detail: err.error.message,
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Creazione schedulazione',
                detail: 'Errore durante la creazione della schedulazione.',
              });
            }
          },
        });
      }
    } catch (error) {}
    // this.ref.close({
    //   value: {
    //     name: this.scheduleForm.get('name')!.value,
    //     maintenance: this.scheduleForm.get('maintenance')!.value,
    //   },
    // });
  }

  titleModal(schedule: any): string {
    return `Allegati per schedulazione ticket ${this.targetTicket?.ticket_number}/${
      this.targetTicket?.ticket_subnumber
    } del ${formatDate(
      schedule.created_on,
      'dd/MM/yyyy HH:mm',
      'it'
    )} per ${schedule.related_registry?.name} ${
      schedule.assigned_user
        ? ` (${schedule.assigned_user?.name}${schedule.assigned_user?.last_name})`
        : ''
    }`;
  }

  openActions(event: Event, selectedSchedule: ScheduleItem) {
    event.stopPropagation();
    this.selectedSchedule = selectedSchedule;
    const hasConfiguration = !!this.scheduleConfiguration;
    const grants = this.grantsService.grantList;

    const grantCheck = (permissions: string[]) => checkGrants(permissions, grants);

    const grantScheduleR = grantCheck(['TICKET-SCHEDULES.R']);
    const grantScheduleC = grantCheck(['TICKET-SCHEDULES.C']);
    const grantScheduleD = grantCheck(['TICKET-SCHEDULES.D']);
    const grantScheduleU = grantCheck(['TICKET-SCHEDULES.U']);

    this.tbl_actions = [
      {
        label: 'Invia email schedulazione',
        icon: 'pi pi-send',
        visible: grantScheduleU || grantScheduleC,
        disabled: !hasConfiguration,
        tooltip: !hasConfiguration
          ? "Impossibile effettuare l'operazione, configurazione email mancante. Allegane una nella lista delle commesse."
          : undefined,
        command: event => {
          this.scheduleService
            .scheduleControllerFindOneSchedule({ id: selectedSchedule.id })
            .subscribe({
              next: res => {
                this.sendEmail(res.data);
              },
              error: error => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Email schedulazione',
                  detail: 'Errore durante il recupero della schedulazione.',
                });
              },
            });
          // this.isEditable(this.selectedAsset!.id, event.originalEvent!);
        },
      },
      {
        label: 'Modifica',
        visible: grantScheduleU,
        icon: 'pi pi-pencil',
        command: event => {
          this.formTitle = 'Modifica schedulazione';
          this.isEditMode = true;
          this.updateSchedule(this.selectedSchedule?.id!);
          // this.isEditable(this.selectedAsset!.id, event.originalEvent!);
        },
      },
      {
        label: 'Elimina',
        visible: grantScheduleD,
        icon: 'pi pi-trash',
        command: event => {
          this.confirmationDeleteDialog(this.selectedSchedule?.id!, event.originalEvent!);
          // this.showDialog(this.selectedAsset!.id, event.originalEvent!);
        },
      },
    ];
  }

  private sendEmail(schedule: ScheduleItem) {
    const ref = this.dialogService.open(SendEmailGenericComponent, {
      appendTo: 'body',
      header: `Invia email schedulazione`,
      width: '60%',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      dismissableMask: false,
      closable: true,
      modal: true,
      data: {
        type: 'schedule',
        detail: schedule,
      },
    });
    ref.onClose.subscribe({
      next: response => {
        this.router.navigate(['/main', 'ticket']);
        console.log('closed');
      },
    });
  }
}
